import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Checkbox } from '@/components/ui/checkbox'
import { PhoneInput } from '@/components/ui/phone-input'
import { vendorsService } from '@/services/vendorsService'
import { VendorViewModel, CountryLookup, CityLookup, DOCUMENT_TABLE } from '@/api/types'
import { EntityDocuments, EntityDocumentsHandle } from '@/components/shared/EntityDocuments'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { showSuccess } from '@/lib/swal'

/**
 * Vendor Detail/Form Page
 * For creating and editing vendors
 */
export const VendorsEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId

  const isEditMode = id !== 'new' && id !== undefined
  const documentsRef = useRef<EntityDocumentsHandle>(null)

  const [vendor, setVendor] = useState<VendorViewModel>({
    vendorId: 0,
    vendorNameEn: '',
    vendorNameAr: '',
    primaryPerson: '',
    email: '',
    phoneNo: '',
    mobileNo: '',
    website: '',
    address: '',
    city: 0,
    state: 0,
    zipCode: '',
    country: 0,
    companyRegistrationNo: '',
    gstNo: '',
    activeStatus: true,
    branchId: branchId || 0,
    ipAddress: '0.0.0.0',
  })

  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cities, setCities] = useState<CityLookup[]>([])
  const [states, setStates] = useState<CityLookup[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const arabicFieldProps = {
    dir: 'rtl' as const,
    className: 'text-right',
  }

  // Load countries
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countriesData = await vendorsService.getCountries()
        setCountries(countriesData)
      } catch (error: any) {
        toast.error(t('vendors.failedToLoadCountries'))
      }
    }
    loadCountries()
  }, [])

  // Load states
  useEffect(() => {
    const loadStates = async () => {
      try {
        const statesData = await vendorsService.getStates()
        setStates(statesData)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadStates()
  }, [])

  // Load cities when country changes
  useEffect(() => {
    if (vendor.country && vendor.country > 0) {
      const loadCities = async () => {
        try {
          const citiesData = await vendorsService.getCities(vendor.country)
          setCities(citiesData)
        } catch (error: any) {
          // Error handled silently
        }
      }
      loadCities()
    } else {
      setCities([])
    }
  }, [vendor.country])

  // Load vendor data if editing
  useEffect(() => {
    if (!isEditMode || !branchId || !id || countries.length === 0) return

    const loadVendor = async () => {
      try {
        setLoading(true)
        const vendorId = Number(id)
        const vendorData = await vendorsService.getVendorById(branchId, vendorId)
        
        const countryId = vendorData.country || 0
        const cityId = vendorData.city || 0
        const stateId = vendorData.state || 0
        
        // Load cities if country is selected
        let citiesData: CityLookup[] = []
        if (countryId > 0) {
          try {
            citiesData = await vendorsService.getCities(countryId)
            setCities(citiesData)
          } catch (error) {
            console.error('Failed to load cities', error)
          }
        }
        
        setVendor({
          vendorId: vendorData.vendorId || vendorData.id || 0,
          vendorNameEn: vendorData.vendorNameEn || '',
          vendorNameAr: vendorData.vendorNameAr || '',
          primaryPerson: vendorData.primaryPerson || '',
          email: vendorData.email || '',
          phoneNo: vendorData.phoneNo || '',
          mobileNo: vendorData.mobileNo || '',
          website: vendorData.website || '',
          address: vendorData.address || '',
          city: cityId,
          state: stateId,
          zipCode: vendorData.zipCode || '',
          country: countryId,
          companyRegistrationNo: vendorData.companyRegistrationNo || '',
          gstNo: vendorData.gstNo || '',
          activeStatus: vendorData.activeStatus !== undefined ? vendorData.activeStatus : true,
          branchId: vendorData.branchId || branchId,
          ipAddress: '0.0.0.0',
        })
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('vendors.failedToLoadVendor'))
      } finally {
        setLoading(false)
      }
    }

    loadVendor()
  }, [isEditMode, branchId, id, countries])

  // Get user IP address (logged-in user's IP, not server's IP)
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error(t('vendors.pleaseSelectBranch'))
      return
    }

    if (!vendor.vendorNameEn && !vendor.vendorNameAr) {
      toast.error(t('vendors.pleaseEnterVendorName'))
      return
    }

    try {
      setSaving(true)

      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()
      
      // Prepare payload matching exact API structure
      const vendorData: VendorViewModel = {
        vendorId: vendor.vendorId || 0,
        vendorNameEn: vendor.vendorNameEn || '',
        vendorNameAr: vendor.vendorNameAr || '',
        primaryPerson: vendor.primaryPerson || '',
        email: vendor.email || '',
        phoneNo: vendor.phoneNo || '',
        mobileNo: vendor.mobileNo || '',
        website: vendor.website || '',
        address: vendor.address || '',
        city: vendor.city || 0,
        state: vendor.state || 0,
        zipCode: vendor.zipCode || '',
        country: vendor.country || 0,
        companyRegistrationNo: vendor.companyRegistrationNo || '',
        gstNo: vendor.gstNo || '',
        activeStatus: vendor.activeStatus !== undefined ? vendor.activeStatus : true,
        branchId: branchId || 0,
        ipAddress: ipAddress, // Set logged-in user's IP address
      }

      if (isEditMode) {
        await vendorsService.updateVendor(vendorData)
        await showSuccess(
          t('vendors.updatedTitle', { defaultValue: 'Updated' }),
          t('vendors.vendorUpdatedSuccess'),
          2500
        )
        navigate('/purchases/vendors')
        return
      }

      const saved = await vendorsService.saveVendor(vendorData)
      const newId = Number(saved.vendorId || saved.id || 0)
      let documentsError: string | null = null
      if (newId > 0) {
        try {
          await documentsRef.current?.uploadPending(newId)
        } catch (error: any) {
          documentsError = error?.message || t('entityDocuments.failedToUpload')
        }
      }

      await showSuccess(
        t('vendors.savedTitle', { defaultValue: 'Saved' }),
        t('vendors.vendorCreatedSuccess'),
        2500
      )
      if (documentsError) {
        toast.error(documentsError)
      }
      if (newId > 0) {
        navigate(`/purchases/vendors/${newId}/edit`, { replace: true })
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isEditMode ? t('vendors.failedToUpdateVendor') : t('vendors.failedToSaveVendor')))
    } finally {
      setSaving(false)
    }
  }

  const countryOptions: SearchableSelectOption[] = countries.map((country) => ({
    value: String(country.id || 0),
    label: language === 'ar' 
      ? (country.lookupNameAr || country.lookupNameEn || '') 
      : (country.lookupNameEn || country.lookupNameAr || ''),
  }))

  const cityOptions: SearchableSelectOption[] = cities.map((city) => ({
    value: String(city.id || 0),
    label: language === 'ar' 
      ? (city.lookupNameAr || city.lookupNameEn || '') 
      : (city.lookupNameEn || city.lookupNameAr || ''),
  }))

  const stateOptions: SearchableSelectOption[] = states.map((state) => ({
    value: String(state.id || 0),
    label: language === 'ar' 
      ? (state.lookupNameAr || state.lookupNameEn || '') 
      : (state.lookupNameEn || state.lookupNameAr || ''),
  }))

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('vendors.title')}
        description={isEditMode ? t('vendors.updateVendorInformation') : t('vendors.createNewVendor')}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
      >
        <div className="space-y-6">
          {/* Vendor Info Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('vendors.vendorInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vendorNameEn">{t('vendors.name')}</Label>
                  <Input
                    id="vendorNameEn"
                    value={vendor.vendorNameEn || ''}
                    onChange={(e) => setVendor({ ...vendor, vendorNameEn: e.target.value })}
                    placeholder={t('vendors.enterVendorName')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendorNameAr">{t('vendors.nameArabic')}</Label>
                  <Input
                    id="vendorNameAr"
                    {...arabicFieldProps}
                    value={vendor.vendorNameAr || ''}
                    onChange={(e) => setVendor({ ...vendor, vendorNameAr: e.target.value })}
                    placeholder={t('vendors.enterVendorNameArabic')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="primaryPerson">{t('vendors.primaryPerson')}</Label>
                  <Input
                    id="primaryPerson"
                    value={vendor.primaryPerson || ''}
                    onChange={(e) => setVendor({ ...vendor, primaryPerson: e.target.value })}
                    placeholder={t('vendors.enterPrimaryContactPerson')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('vendors.contactDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneNo">{t('vendors.phoneNo')}</Label>
                  <Input
                    id="phoneNo"
                    value={vendor.phoneNo || ''}
                    onChange={(e) => setVendor({ ...vendor, phoneNo: e.target.value })}
                    placeholder={t('vendors.enterPhoneNumber')}
                  />
                </div>
                <div className="space-y-2">
                  <PhoneInput
                    key={`mobile-${vendor.vendorId || 'new'}`}
                    id="mobileNo"
                    label={t('vendors.mobileNo')}
                    value={vendor.mobileNo || ''}
                    onChange={(value) => setVendor({ ...vendor, mobileNo: value })}
                    placeholder={t('vendors.enterMobileNumber')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">{t('vendors.website')}</Label>
                  <Input
                    id="website"
                    value={vendor.website || ''}
                    onChange={(e) => setVendor({ ...vendor, website: e.target.value })}
                    placeholder={t('vendors.enterWebsiteUrl')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('vendors.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={vendor.email || ''}
                    onChange={(e) => setVendor({ ...vendor, email: e.target.value })}
                    placeholder={t('vendors.enterEmailAddress')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('vendors.addressDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">{t('vendors.country')}</Label>
                  <SearchableSelect
                    options={countryOptions}
                    value={vendor.country ? String(vendor.country) : ''}
                    onValueChange={(value) => {
                      setVendor({ 
                        ...vendor, 
                        country: Number(value) || 0,
                        city: 0,
                        state: 0
                      })
                    }}
                    placeholder={t('vendors.selectCountry')}
                    searchPlaceholder={t('vendors.searchCountries')}
                    emptyMessage={t('vendors.noCountriesFound')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">{t('vendors.city')}</Label>
                  <SearchableSelect
                    options={cityOptions}
                    value={vendor.city ? String(vendor.city) : ''}
                    onValueChange={(value) => {
                      setVendor({ 
                        ...vendor, 
                        city: Number(value) || 0
                      })
                    }}
                    placeholder={t('vendors.selectCity')}
                    searchPlaceholder={t('vendors.searchCities')}
                    emptyMessage={t('vendors.noCitiesFound')}
                    disabled={!vendor.country || vendor.country === 0}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zipCode">{t('vendors.zipCode')}</Label>
                  <Input
                    id="zipCode"
                    value={vendor.zipCode || ''}
                    onChange={(e) => setVendor({ ...vendor, zipCode: e.target.value })}
                    placeholder={t('vendors.enterZipCode')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">{t('vendors.state')}</Label>
                  <SearchableSelect
                    options={stateOptions}
                    value={vendor.state ? String(vendor.state) : ''}
                    onValueChange={(value) => {
                      setVendor({ 
                        ...vendor, 
                        state: Number(value) || 0
                      })
                    }}
                    placeholder={t('vendors.selectState')}
                    searchPlaceholder={t('vendors.searchStates')}
                    emptyMessage={t('vendors.noStatesFound')}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">{t('vendors.address')}</Label>
                  <Textarea
                    id="address"
                    value={vendor.address || ''}
                    onChange={(e) => setVendor({ ...vendor, address: e.target.value })}
                    placeholder={t('vendors.enterFullAddress')}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="activeStatus">{t('vendors.activeStatus')}</Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="activeStatus"
                      checked={vendor.activeStatus !== undefined ? vendor.activeStatus : true}
                      onCheckedChange={(checked) => setVendor({ ...vendor, activeStatus: checked === true })}
                    />
                    <Label htmlFor="activeStatus" className="font-normal cursor-pointer">
                      {t('common.active')}
                    </Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tax Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('vendors.taxDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyRegistrationNo">{t('vendors.companyRegistrationNo')}</Label>
                  <Input
                    id="companyRegistrationNo"
                    value={vendor.companyRegistrationNo || ''}
                    onChange={(e) => setVendor({ ...vendor, companyRegistrationNo: e.target.value })}
                    placeholder={t('vendors.enterRegistrationNumber')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gstNo">{t('vendors.gstNo')}</Label>
                  <Input
                    id="gstNo"
                    value={vendor.gstNo || ''}
                    onChange={(e) => setVendor({ ...vendor, gstNo: e.target.value })}
                    placeholder={t('vendors.enterGstNo')}
                  />
                </div>
              </div>
          </CardContent>
        </Card>

        <EntityDocuments
          ref={documentsRef}
          tableName={DOCUMENT_TABLE.vendor}
          tableRowId={Number(id) || 0}
        />

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/purchases/vendors')} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">
              {saving ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('vendors.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditMode ? t('vendors.updateVendor') : t('vendors.saveVendor')}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

